import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useCreateSupplier } from "../../hooks/api/useSupplier";
import {
  Address,
  BRAND_COLOR,
  BRAND_HOVER,
  PhoneCountryCodeMap,
  combinePhoneNumber,
  validatePhoneWithCountrySelection,
  getDefaultCountryCode,
  useKivunovaTranslation,
  HandledGraphQLMutationError,
  translateSupplierCreateConflict
} from "@kivunova/kivufrontendcommon";
import useMediaQuery from "@mui/material/useMediaQuery";
import AddressForm from "../common/AddressForm/AddressForm";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { UserRole } from "../Profile/enums/profileEnums";

import { AddressInput, CreateSupplierInput } from "./types/supplier";

interface AddSupplierModalProps {
  open: boolean;
  onClose: () => void;
  /** Store from route; company admins submit suppliers for this store (no store picker). */
  fixedStoreId?: string;
}

interface SupplierFormData {
  storeId?: string;
  name: string;
  email?: string;
  phone: string;
  phoneCountryCode: string;
  address?: Address;
}

const AddSupplierModal: React.FC<AddSupplierModalProps> = ({
  open,
  onClose,
  fixedStoreId
}) => {
  const createSupplier = useCreateSupplier();
  const [submitApiError, setSubmitApiError] = useState<string | null>(null);
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useKivunovaTranslation();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const isCompanyAdmin =
    String(userDetails?.role ?? "").trim() === UserRole.COMPANY_ADMIN;
  const lockedStoreId = String(fixedStoreId ?? "").trim();

  const supplierSchema = useMemo(() => {
    const base = {
      name: Yup.string().required(
        t("supplier-name-required", "Name is required")
      ),
      email: Yup.string()
        .email(t("supplier-email-invalid", "Invalid email format"))
        .test(
          "supplier-phone-or-email",
          t(
            "supplier-phone-or-email-required",
            "Provide a phone number or an email address"
          ),
          function (value) {
            const email = (value ?? "").trim();
            const phone = (
              (this.parent as { phone?: string }).phone ?? ""
            ).trim();
            return Boolean(phone || email);
          }
        )
        .optional(),
      phone: Yup.string()
        .optional()
        .test(
          "phone-validation",
          t(
            "supplier-phone-invalid",
            "Invalid phone number for selected country"
          ),
          function (value) {
            if (!value || value.trim() === "") {
              return true;
            }
            const { phoneCountryCode } = this.parent;
            return validatePhoneWithCountrySelection(value, phoneCountryCode);
          }
        ),
      phoneCountryCode: Yup.string().when("phone", {
        is: (phone: string) => Boolean(phone && String(phone).trim()),
        then: (schema) =>
          schema.required(
            t("supplier-country-code-required", "Country code is required")
          ),
        otherwise: (schema) => schema.optional()
      }),
      address: Yup.object()
        .shape({
          country: Yup.string().optional().nullable(),
          province: Yup.string().optional().nullable(),
          district: Yup.string().optional().nullable(),
          sector: Yup.string().optional().nullable(),
          cell: Yup.string().optional().nullable(),
          village: Yup.string().optional().nullable(),
          streetAddress: Yup.string().optional().nullable()
        })
        .optional()
        .nullable()
    };
    const needsStore = isCompanyAdmin && Boolean(lockedStoreId);
    return Yup.object().shape({
      ...base,
      storeId: needsStore
        ? Yup.string().required(t("store-required", "Store is required"))
        : Yup.string().optional()
    });
  }, [t, isCompanyAdmin, lockedStoreId]);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue
  } = useForm<SupplierFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(supplierSchema as any),
    mode: "onChange",
    defaultValues: {
      storeId: "",
      name: "",
      email: "",
      phone: "",
      phoneCountryCode: getDefaultCountryCode(),
      address: {
        country: "Rwanda",
        province: "",
        district: "",
        sector: "",
        cell: "",
        village: "",
        streetAddress: ""
      }
    }
  });

  useEffect(() => {
    if (!open || !isCompanyAdmin || !lockedStoreId) return;
    setValue("storeId", lockedStoreId);
  }, [open, isCompanyAdmin, lockedStoreId, setValue]);

  useEffect(() => {
    if (open) setSubmitApiError(null);
  }, [open]);

  const onSubmit: SubmitHandler<SupplierFormData> = async (data) => {
    try {
      const fullPhoneNumber =
        data.phone && data.phone.trim() !== ""
          ? combinePhoneNumber(
              data.phoneCountryCode as string,
              data.phone,
              PhoneCountryCodeMap
            )
          : null;

      const addr = data.address;
      const hasAddress =
        addr &&
        [
          addr.country,
          addr.province,
          addr.district,
          addr.sector,
          addr.cell,
          addr.village,
          addr.streetAddress
        ].some((x) => String(x ?? "").trim() !== "");

      const resolvedStoreId = (data.storeId?.trim() || lockedStoreId) ?? "";
      const addSupplierData: CreateSupplierInput = {
        name: data.name,
        ...(isCompanyAdmin && resolvedStoreId
          ? { storeId: resolvedStoreId }
          : {}),
        email: data.email?.trim() ? data.email.trim() : null,
        phone: fullPhoneNumber,
        address: hasAddress
          ? ({
              country: addr!.country,
              province: addr!.province,
              district: addr!.district,
              sector: addr!.sector,
              cell: addr!.cell,
              village: addr!.village,
              streetAddress: addr!.streetAddress
            } as AddressInput)
          : null
      };

      await createSupplier.mutateAsync(addSupplierData);
      reset();
      onClose();
    } catch (error) {
      if (error instanceof HandledGraphQLMutationError && error.apiCode) {
        const msg = translateSupplierCreateConflict(t, error.apiCode);
        setSubmitApiError(
          msg ??
            t(
              "create-supplier-failed",
              "Could not create supplier. Please try again."
            )
        );
        return;
      }
      console.error("Failed to create supplier:", error);
    }
  };

  const isLoading = isSubmitting || createSupplier.isPending;

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="modal-styles">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: matchMD ? 2 : 3,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0
          }}
        >
          <Typography
            variant={matchMD ? "h6" : "h5"}
            fontWeight="bold"
            color="textPrimary"
          >
            {t("supplier-add-title", "Add Supplier")}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            overflow: "hidden"
          }}
        >
          {/* Scrollable Content Area */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: matchMD ? 2 : 3,
              pt: 2,
              display: "flex",
              flexDirection: "column",
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
                borderRadius: "10px"
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px",
                "&:hover": { backgroundColor: "#555" }
              }
            }}
          >
            {/* Form Fields - Vertical Layout */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: matchMD ? 1.5 : 2
              }}
            >
              {submitApiError ? (
                <Alert
                  severity="error"
                  onClose={() => setSubmitApiError(null)}
                  sx={{ mb: 0.5 }}
                >
                  {submitApiError}
                </Alert>
              ) : null}
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size={matchMD ? "small" : "medium"}
                    label={t("supplier-field-name", "Name")}
                    placeholder={t(
                      "supplier-field-name-placeholder",
                      "Enter supplier name"
                    )}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    disabled={isLoading}
                  />
                )}
              />

              {/* Phone Number with Country Code */}
              <Box
                sx={{
                  display: "flex",
                  gap: matchMD ? 1 : 2,
                  alignItems: "flex-start",
                  flexDirection: matchMD ? "column" : "row"
                }}
              >
                <Controller
                  name="phoneCountryCode"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      sx={{
                        flex: matchMD ? 1 : "0 0 160px",
                        minWidth: matchMD ? "100%" : 140,
                        width: matchMD ? "100%" : "auto"
                      }}
                      variant="outlined"
                      error={!!errors.phoneCountryCode}
                      disabled={isLoading}
                      size={matchMD ? "small" : "medium"}
                    >
                      <InputLabel id="phone-country-code-label">
                        {t("country-code", "Code")}
                      </InputLabel>
                      <Select
                        {...field}
                        value={field.value || getDefaultCountryCode()}
                        labelId="phone-country-code-label"
                        id="phone-country-code"
                        label={t("country-code", "Code")}
                        disabled={isLoading}
                      >
                        {Object.keys(PhoneCountryCodeMap).map((key) => (
                          <MenuItem key={key} value={key}>
                            {`${PhoneCountryCodeMap[key].flag} ${PhoneCountryCodeMap[key].code}`}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.phoneCountryCode && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, ml: 1.75 }}
                        >
                          {errors.phoneCountryCode.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />

                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t(
                        "supplier-phone-optional-if-email",
                        "Phone (optional if email)"
                      )}
                      fullWidth
                      size={matchMD ? "small" : "medium"}
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                      placeholder="782484464"
                      disabled={isLoading}
                    />
                  )}
                />
              </Box>

              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size={matchMD ? "small" : "medium"}
                    label={t(
                      "supplier-email-optional-if-phone",
                      "Email (optional if phone)"
                    )}
                    placeholder={t(
                      "supplier-field-email-placeholder",
                      "Enter email"
                    )}
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={isLoading}
                  />
                )}
              />

              <Paper
                variant="outlined"
                sx={{
                  p: matchMD ? 1.5 : 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: matchMD ? 1.5 : 2
                }}
              >
                <Typography
                  variant={matchMD ? "body2" : "subtitle1"}
                  fontWeight={600}
                  color="textPrimary"
                >
                  {t("address", "Address")}
                </Typography>
                <AddressForm
                  control={control}
                  defaultAddress={useWatch({ control, name: "address" })}
                  setValue={setValue}
                />
              </Paper>
            </Box>
          </Box>

          {/* Fixed Action Buttons */}
          <Box
            sx={{
              display: "flex",
              flexDirection: matchMD ? "column" : "row",
              gap: matchMD ? 1 : 2,
              p: matchMD ? 2 : 3,
              pt: matchMD ? 1.5 : 2,
              borderTop: "1px solid",
              borderColor: "divider",
              flexShrink: 0,
              backgroundColor: "background.paper"
            }}
          >
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isLoading}
              fullWidth
              size={matchMD ? "medium" : "large"}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                backgroundColor: BRAND_COLOR,
                "&:hover": { backgroundColor: BRAND_HOVER }
              }}
            >
              {t("supplier-create-submit", "Create Supplier")}
            </LoadingButton>

            <Button
              type="button"
              variant="outlined"
              onClick={onClose}
              disabled={isLoading}
              fullWidth
              size={matchMD ? "medium" : "large"}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                backgroundColor: "white",
                color: BRAND_COLOR,
                border: `1px solid ${BRAND_COLOR}`,
                "&:hover": {
                  backgroundColor: BRAND_HOVER,
                  color: "white",
                  borderColor: BRAND_HOVER
                }
              }}
            >
              {t("cancel", "Cancel")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddSupplierModal;
